import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Cookies from 'universal-cookie';
import { DBRequest, useConfig } from '../../api/api';
import { useVisibleInterval } from '../../api/useVisibleInterval';

const EmailClient = createContext({
  preferences: null,
  updateSelectedFolder: () => {},
});

const cookies = new Cookies();

// While an email client is on screen new mail should show up quickly; on every
// other page only the badge counts need to stay roughly current. Polling stops
// completely while the app is in the background (see useVisibleInterval).
const POLL_MS_CLIENT_OPEN = 30 * 1000;
const POLL_MS_BACKGROUND = 120 * 1000;

// Even when nothing changed, take a fresh response now and then so signed
// URLs inside it never expire.
const MAX_SKIPPED_UPDATE_MS = 10 * 60 * 1000;

const ONE_HOUR_MS = 60 * 60 * 1000;

// Compares responses without the fields that change on every request but don't
// change what is shown: signed URLs, and sync timestamps (only "older than one
// hour" is displayed).
function getFingerprint(data) {
  return JSON.stringify(data, (key, value) => {
    if (key === 'temporaryUrl' || key === 'thumbnailUrl') return undefined;
    if (key === 'lastSuccessfulSyncAt')
      return value ? Date.now() - new Date(value).getTime() > ONE_HOUR_MS : null;
    return value;
  });
}

// credentials whose IMAP connection is no longer authenticated (imapStatus !== 1)
function getReauthInboxes(arrOfInboxes) {
  return arrOfInboxes?.filter((inbox) => inbox.imapStatus !== 1) || [];
}

function getUnseenCount(arrOfInboxes) {
  let count = 0;
  arrOfInboxes?.forEach((element) => {
    element.inbox?.folders?.forEach((folder) => {
      if (folder.name === 'INBOX') count += folder.unseenCount;
    });
  });
  return count;
}

function sumCredentialField(credentials, field) {
  return (credentials || []).reduce((sum, cred) => sum + (cred[field] || 0), 0);
}

export const EmailClientProvider = ({ children }) => {
  const config = useConfig();
  const [apiData, setApiData] = useState(null);
  const apiDataRef = useRef(null);
  apiDataRef.current = apiData;

  const [unseenCount, setUnseenCount] = useState(null);
  const [failedCount, setFailedCount] = useState(null);
  const [queuedCount, setQueuedCount] = useState(null);
  // drafts prepared by AI agents that wait for the user's review
  const [aiDraftCount, setAiDraftCount] = useState(null);
  // inboxes whose connection is broken and need re-authentication
  const [reauthInboxes, setReauthInboxes] = useState([]);
  // The vacation driven auto reply. Kept here rather than in the settings
  // dialog so the inbox list can mark the mailboxes it answers for without
  // opening settings first. null = not loaded yet.
  const [vacationNotification, setVacationNotification] = useState(null);

  // number of mounted email clients; while one is open, polling is faster
  const [openClients, setOpenClients] = useState(0);

  const lastFingerprintRef = useRef(null);
  const lastAppliedAtRef = useRef(0);

  const handleResponse = useCallback((response) => {
    const fingerprint = getFingerprint(response?.data);
    const isUnchanged =
      fingerprint === lastFingerprintRef.current &&
      Date.now() - lastAppliedAtRef.current < MAX_SKIPPED_UPDATE_MS;
    // Skipping identical responses keeps every consumer (the whole email
    // client, Sidebar, composer …) from re-rendering on each poll.
    if (isUnchanged) return;

    lastFingerprintRef.current = fingerprint;
    lastAppliedAtRef.current = Date.now();

    setApiData(response);
    setUnseenCount(getUnseenCount(response.data));
    setFailedCount(sumCredentialField(response.data, 'failedEmailCount'));
    setQueuedCount(sumCredentialField(response.data, 'queuedEmailCount'));
    setAiDraftCount(sumCredentialField(response.data, 'pendingAiDraftCount'));
    setReauthInboxes(getReauthInboxes(response.data));
  }, []);

  // Local edits (settings, folder changes) replace the data directly; the next
  // poll must then always be applied, even if it equals the last poll.
  const setEmailCredentials = useCallback((next) => {
    lastFingerprintRef.current = null;
    setApiData(next);
  }, []);

  const fetchFolders = useCallback(() => {
    // No session (public pages) — skip the authenticated request.
    if (!config || !cookies.get('accessToken')) return undefined;
    return DBRequest({
      config,
      path: `email-credentials`,
      onResponse: handleResponse,
    });
  }, [config, handleResponse]);

  // initial load
  useEffect(() => {
    if (config && !apiDataRef.current) fetchFolders();
  }, [config, fetchFolders]);

  useVisibleInterval(
    () => {
      // accounts without any inbox have nothing to poll
      if (apiDataRef.current?.data?.length === 0) return undefined;
      return fetchFolders();
    },
    openClients > 0 ? POLL_MS_CLIENT_OPEN : POLL_MS_BACKGROUND,
    { enabled: Boolean(config) },
  );

  // Called by EmailClient while it is mounted. Opening the client also
  // refreshes right away, so the user never starts with stale counts.
  const registerClient = useCallback(() => {
    setOpenClients((count) => count + 1);
    if (Date.now() - lastAppliedAtRef.current > 5000) fetchFolders();
    return () => setOpenClients((count) => Math.max(0, count - 1));
  }, [fetchFolders]);

  // Listen for standalone auth changes (re-login)
  useEffect(() => {
    const handleAuthChange = (event) => {
      if (event.detail?.authenticated && config) {
        // Small delay to ensure cookies are set
        setTimeout(() => {
          fetchFolders();
        }, 100);
      }
    };

    window.addEventListener('standaloneAuthChange', handleAuthChange);
    return () =>
      window.removeEventListener('standaloneAuthChange', handleAuthChange);
  }, [config, fetchFolders]);

  // Deliberately not part of the folder poll above — this changes only when the
  // user edits it, so it is read once and then kept in sync by the settings tab.
  const fetchVacationNotification = useCallback(() => {
    // No session (public pages) — skip the authenticated request.
    if (!config || !cookies.get('accessToken')) return;
    DBRequest({
      config,
      path: 'absence-notifications/vacation',
      onResponse: (res) =>
        setVacationNotification(res?.data?.data || res?.data || {}),
      // An empty object still resolves the loading state; with no `isEnabled`
      // it simply marks nothing.
      onError: () => setVacationNotification({}),
    });
  }, [config]);

  useEffect(() => {
    if (config) fetchVacationNotification();
  }, [config, fetchVacationNotification]);

  const value = useMemo(
    () => ({
      emailCredentials: apiData,
      setEmailCredentials,
      fetchFolders,
      registerClient,
      unseenCount,
      failedCount,
      queuedCount,
      aiDraftCount,
      reauthInboxes,
      reauthCount: reauthInboxes.length,
      vacationNotification,
      setVacationNotification,
      fetchVacationNotification,
    }),
    [
      apiData,
      setEmailCredentials,
      fetchFolders,
      registerClient,
      unseenCount,
      failedCount,
      queuedCount,
      aiDraftCount,
      reauthInboxes,
      vacationNotification,
      fetchVacationNotification,
    ],
  );

  return <EmailClient.Provider value={value}>{children}</EmailClient.Provider>;
};

export const useEmailClient = () => useContext(EmailClient);
